import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { compact, isEmpty, toNumber } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';
import { faSortAmountAsc } from '@fortawesome/free-solid-svg-icons';
import { createBemBlockBuilder, emitScrollTo } from '@ebsco-ui/ebsco-ui';

import {
  ComponentSpinner,
  Dropdown,
  PageErrorMessage,
  Pagination,
  RecordLinks,
  RecordSummary,
  SearchInstanceDto,
  searchResultSortItems,
  SortByDropdown,
  useRecentActivityHistory,
} from '@app/components';
import {
  useAppContext,
  useAvailabilityContext,
  useMappingsContext,
  useSearchContext,
} from '@app/contexts';
import {
  useFeatureFlag,
  useHoldsAndCheckoutsPageConfig,
  usePaginationItemFocus,
} from '@app/hooks';
import { getItemStatusData, getItemWithStatus, setFocusOn } from '@app/utils';
import { sharedMessages } from '@app/translations';
import {
  ADVANCED_SEARCH_ERROR,
  BREAKPOINT,
  FEATURE,
  RECORDS_PER_PAGE,
} from '@app/constants';

import { PassthroughLink } from './PassthroughLink';
import { FilterPanel } from './FilterPanel';

import './SearchResultsPage.scss';

const cnBem = createBemBlockBuilder(['searchResultsPage']);

export const SearchResultsPage: FC = () => {
  const intl = useIntl();
  const { $t, formatNumber } = intl;
  const { locale } = useAppContext();
  const { userItemStatuses } = useMappingsContext();
  const RTACForSearchFlag = useFeatureFlag({
    name: FEATURE.RTACForSearch,
    isActiveIfNotFound: true,
  });
  const { addSearchHistoryEntry } = useRecentActivityHistory();
  const { selectConfiguredOption: selectConfiguredHoldsAndCheckoutsOption } =
    useHoldsAndCheckoutsPageConfig();
  const {
    state,
    searchOption,
    shouldRefocusRef,
    searchResults: {
      searchResultsQuery: {
        isFetching,
        isRefetching,
        isError,
        error,
        fetchStatus,
        status,
        refetch,
      },
      pages,
      currentPage,
      totalRecords = 0,
    },
    updateState,
    instanceIdScrollTo,
    isPageFromBackButton,
    setInstanceIdScrollTo,
    setIsPageFromBackButton,
  } = useSearchContext();
  const {
    query,
    sortField,
    sortDirection,
    pageNumber,
    recordsPerPage = RECORDS_PER_PAGE,
  } = state;

  const [isNewPageLoading, setIsNewPageLoading] = useState(false);
  const { getAvailability, availability, isAvailabilityLoading } =
    useAvailabilityContext();
  const mainContainerRef = useRef<HTMLDivElement>(null!);
  const { search: urlQuery } = useLocation();
  const isSearchPerformed = fetchStatus !== 'fetching' && status !== 'loading';
  const isMobile = useMediaQuery(
    `(max-width: ${BREAKPOINT.SCREEN.TABLET_LG - 1}px)`
  );

  const isSearchSuccessful =
    !isRefetching && status === 'success' && !isEmpty(currentPage);

  const getRecordsIds = (records: SearchInstanceDto[] = []) =>
    records.map(record => record.id);

  usePaginationItemFocus({
    pageNumber,
    totalPagesNumber: pages.length,
    instanceIdScrollTo,
  });

  useEffect(() => {
    if (isSearchSuccessful && RTACForSearchFlag.isActive) {
      getAvailability(getRecordsIds(currentPage), pageNumber);
    }
  }, [
    getAvailability,
    isSearchSuccessful,
    currentPage,
    pageNumber,
    RTACForSearchFlag?.isActive,
  ]);

  useEffect(() => {
    if (!isFetching) {
      setIsNewPageLoading(false);
    }
  }, [setIsNewPageLoading, isFetching]);

  const handleSortBySelect = useCallback(
    option => {
      updateState({
        sortField: option.sortField,
        sortDirection: option.sortDirection,
        option: searchOption,
      });
      shouldRefocusRef.current = true;
    },
    [updateState, searchOption, shouldRefocusRef]
  );

  const titlesPerPageDropdownItemLabel = $t({
    id: 'searchResults.show',
    defaultMessage: 'Show:',
  });

  const titlesPerPageDropdownItems = [
    {
      label: `${titlesPerPageDropdownItemLabel} ${formatNumber(25)}`,
      mobileLabel: 25,
      value: 25,
    },
    {
      label: `${titlesPerPageDropdownItemLabel} ${formatNumber(50)}`,
      mobileLabel: 50,
      value: 50,
    },
    {
      label: `${titlesPerPageDropdownItemLabel} ${formatNumber(100)}`,
      mobileLabel: 100,
      value: 100,
    },
  ];

  const handleTitlePerPageUpdate = useCallback(
    option => {
      updateState({
        recordsPerPage: toNumber(option.selectedItem.value),
      });
      shouldRefocusRef.current = true;
    },
    [updateState, shouldRefocusRef]
  );

  const renderPageStatus = () => {
    if (isEmpty(pages) && isFetching) {
      return <ComponentSpinner />;
    }

    if (isEmpty(currentPage) && isError) {
      if (
        error &&
        error.errors?.[0].code === ADVANCED_SEARCH_ERROR.CODE &&
        error.errors?.[0].type === ADVANCED_SEARCH_ERROR.TYPE
      ) {
        return (
          <>
            {renderPassthroughLink()}
            <PageErrorMessage
              title={$t({
                id: 'resultsList.searchInvalidTitle',
                defaultMessage: 'Invalid search',
              })}
              text={$t(sharedMessages.searchInvalidText)}
              buttonText=""
              titleTag="h1"
            />
          </>
        );
      }

      return (
        <>
          {renderPassthroughLink()}
          <PageErrorMessage
            title={$t({
              id: 'resultsList.searchUnavailableTitle',
              defaultMessage: 'Search unavailable',
            })}
            titleTag="h1"
            onClick={refetch}
          />
        </>
      );
    }

    const areResultsEmpty = isSearchPerformed && totalRecords === 0;

    if (areResultsEmpty) {
      return (
        <div data-auto="no-search-results" className={cnBem('__no-results')}>
          {renderPassthroughLink()}
          <FormattedMessage
            {...sharedMessages.results}
            values={{ number: 0 }}
            tagName="p"
          />
          <FormattedMessage
            id="resultsList.noResults"
            defaultMessage="No results found"
            tagName="p"
          />
        </div>
      );
    }

    if (!isSearchPerformed && isEmpty(pages)) {
      return (
        <h1 className={cnBem('__messageWrapper')}>
          <FormattedMessage
            id="resultsList.enterQuery"
            defaultMessage="Choose a filter or enter a search query to show results."
          />
        </h1>
      );
    }

    return null;
  };

  const renderPage = () =>
    currentPage?.map(instance => {
      const itemWithStatus = getItemWithStatus(instance.id, userItemStatuses);

      return (
        <RecordSummary
          testId="record-summary"
          key={instance.id}
          instance={instance}
          placement="results"
          itemStatusData={
            itemWithStatus &&
            getItemStatusData(
              itemWithStatus,
              intl,
              locale,
              selectConfiguredHoldsAndCheckoutsOption
            )
          }
          availability={availability[instance.id]?.summaryAvailability}
          isAvailabilityLoading={isAvailabilityLoading}
          isRTACShown={RTACForSearchFlag.isActive}
          links={
            <RecordLinks
              id={instance.id}
              title={instance.title}
              onClick={() => {
                setInstanceIdScrollTo(instance.id);
              }}
            />
          }
        />
      );
    });

  const renderPassthroughLink = () => (
    <PassthroughLink query={query} searchOption={searchOption} />
  );

  useEffect(() => {
    if ((!isFetching || !isEmpty(currentPage)) && shouldRefocusRef.current) {
      mainContainerRef.current.focus({ preventScroll: true });
      emitScrollTo(0, undefined);
      shouldRefocusRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  useEffect(() => {
    if (
      !isFetching &&
      urlQuery &&
      (status === 'success' || status === 'error')
    ) {
      mainContainerRef.current.focus({ preventScroll: true });
      emitScrollTo(0, undefined);
    }

    if (isFetching) {
      shouldRefocusRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (instanceIdScrollTo && !isEmpty(pages)) {
      const selector = `[data-link="${instanceIdScrollTo}-link"]`;

      setFocusOn({ selector });
      setInstanceIdScrollTo(null);
    } else if (!instanceIdScrollTo && isPageFromBackButton) {
      const element = document.querySelector('[data-link]') as HTMLElement;

      setFocusOn({ element });
      setIsPageFromBackButton(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  useEffect(() => {
    if (compact(pages).length === 1) {
      addSearchHistoryEntry(state, totalRecords);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  const titlesPerPageDropdownSelectedItem =
    titlesPerPageDropdownItems.find(item => item.value === recordsPerPage) ??
    titlesPerPageDropdownItems[0];

  return (
    <>
      <Helmet>
        <title>
          {$t(
            {
              id: 'searchResults.documentTitle',
              defaultMessage:
                '{searchTerm, select, null {EBSCO Locate} other {{searchTerm} - Search results - EBSCO Locate}}',
            },
            { searchTerm: query || null }
          )}
        </title>
      </Helmet>
      <div
        className={cn('container', cnBem('__container'))}
        data-auto="search-page-wrapper"
      >
        <FilterPanel />
        <main
          className={cnBem('__wrapper')}
          data-auto="search-results-wrapper"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          ref={mainContainerRef}
          data-focus="focusedNode"
        >
          {renderPageStatus()}
          {!isEmpty(pages) && !isError && totalRecords > 0 && (
            <>
              <div className={cnBem('__panel')}>
                <h1
                  className={cnBem('__count')}
                  data-auto="searchResultsTotalRecords"
                >
                  {totalRecords === 0 ? (
                    <FormattedMessage
                      {...sharedMessages.results}
                      values={{
                        number: <FormattedNumber value={totalRecords} />,
                      }}
                    />
                  ) : (
                    $t(
                      {
                        id: 'searchResults.results',
                        defaultMessage:
                          'Results: <b>{startNumber}-{endNumber}</b> of <b>{totalRecords}</b>',
                      },
                      {
                        startNumber: recordsPerPage * (pageNumber - 1) + 1,
                        endNumber:
                          recordsPerPage * pageNumber > totalRecords
                            ? totalRecords
                            : recordsPerPage * pageNumber,
                        totalRecords: totalRecords.toLocaleString(),
                        b: chunks => <strong>{chunks}</strong>,
                      }
                    )
                  )}
                </h1>
                <div className={cnBem('__dropdownWrapper')}>
                  <Dropdown
                    hideLabel
                    label={titlesPerPageDropdownSelectedItem.label}
                    items={titlesPerPageDropdownItems}
                    id="items-per-page-dropdown"
                    selectedItem={titlesPerPageDropdownSelectedItem}
                    styleType="standard"
                    className={cn('compactDropdown', cnBem('__dropdownItem'))}
                    onSelectedItemChange={handleTitlePerPageUpdate}
                  />
                  <SortByDropdown
                    hideLabel
                    options={searchResultSortItems}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    toggleIcon={faSortAmountAsc}
                    toggleIconOnly={isMobile}
                    styleType="standard"
                    className={cn('compactDropdown', cnBem('__dropdownItem'))}
                    onSelect={handleSortBySelect}
                  />
                </div>
              </div>
              {renderPassthroughLink()}
              {isNewPageLoading && isEmpty(currentPage) && <ComponentSpinner />}
              {renderPage()}
              <Pagination
                pageNumber={pageNumber}
                pageCount={Math.ceil(totalRecords / recordsPerPage)}
                onPageChange={updatePageNumber => {
                  setIsNewPageLoading(true);

                  updateState({
                    pageNumber: updatePageNumber,
                    isNewPageRequest: true,
                  });
                  shouldRefocusRef.current = true;
                }}
              />
            </>
          )}
        </main>
      </div>
    </>
  );
};
